import { zodResolver } from '@hookform/resolvers/zod';
import { Close } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
    useCancelWaitListRequestMutation,
    useCheckSelectedPeriodAvailabilityMutation,
    useGetIndividualAssetQuery,
    useGetIndividualWaitListRequestQuery,
    useUpdateWaitListRequestMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import {
    camelCaseToSentenceCase,
    capitalizeName,
    formatTime,
} from '../../../../shared/utils';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import FormSelect from '../../../shared/FormSelect';
import FormTextField from '../../../shared/FormTextField';
import toast from '../../../shared/Toast';
import { ModalProps } from '../../../types/modal';
import { TimeArray } from '../../Experiences/Owner/Property/TimeArray';
import WaitListPreviewLoading from './WaitListPreviewLoading';
import UserRole from '../../../../shared/AuthRoles';
import { theme } from '../../../../shared/theme';
import CalendarComponent from '../../Experiences/Calendar/CalendarComponent';
import { WaitListRequestStatus } from '../../../../app/Slices/SevenDayApiTypes';

export type CommercialUnits = {
    desks: number;
    privateOffices: number;
    meetingRooms: number;
    entireVenue: boolean;
};

export const mapSpaceTypeToCommercialUnits = (
    spaceType: string,
    reqMeetingRoom: boolean,
): CommercialUnits => ({
    desks: spaceType === 'desk' ? 1 : 0,
    privateOffices: spaceType === 'privateOffice' ? 1 : 0,
    meetingRooms:
        spaceType === 'meetingRoom' ||
        (reqMeetingRoom && spaceType !== 'entireVenue')
            ? 1
            : 0,
    entireVenue: spaceType === 'entireVenue',
});

export const mapCommercialUnitsToSpaceType = (
    units: CommercialUnits,
): { spaceType: string; reqMeetingRoom: boolean } => {
    if (!units) return { spaceType: 'desk', reqMeetingRoom: true };

    const reqMeetingRoom = units.meetingRooms > 0;

    if (units.entireVenue)
        return { spaceType: 'entireVenue', reqMeetingRoom: false };
    if (units.privateOffices > 0)
        return { spaceType: 'privateOffice', reqMeetingRoom };
    if (units.desks > 0) return { spaceType: 'desk', reqMeetingRoom };
    if (units.meetingRooms > 0)
        return { spaceType: 'meetingRoom', reqMeetingRoom: true };

    return { spaceType: '', reqMeetingRoom: false };
};

interface WaitListRequestPreviewProps extends ModalProps {
    isEditingWaitList: boolean;
    setIsEditingWaitList: (data: any) => void;
    id: number;
}

const Schema = z.object({
    startDate: z.string(),
    endDate: z.string(),
    checkinTime: z.string(),
    checkoutTime: z.string(),
    spaceType: z.string().default('desk'),
    reqMeetingRoom: z.boolean().default(false),
    notes: z.string(),
    commercialWaitListRequestInfo: z
        .object({
            desks: z.number(),
            privateOffices: z.number(),
            meetingRooms: z.number(),
            entireVenue: z.boolean(),
        })
        .optional(),
});

const WaitListPreview = ({
    id,
    open,
    onClose,
    isEditingWaitList,
    setIsEditingWaitList,
}: WaitListRequestPreviewProps) => {
    const [updateWaitListRequest] = useUpdateWaitListRequestMutation();
    const [cancelWaitListRequest] = useCancelWaitListRequestMutation();
    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');

    const { data } = useGetIndividualWaitListRequestQuery(id, {
        skip: !id,
    });

    const waitListRequest = open ? data : null;

    const { data: experience } = useGetIndividualAssetQuery(
        `/modules/${waitListRequest?.module?.id}`,
        { skip: !waitListRequest },
    );

    type FormValues = z.infer<typeof Schema>;

    const methods = useForm<FormValues>({
        resolver: zodResolver(Schema),
    });

    useEffect(() => {
        setIsEditingWaitList(false);
    }, []);

    const { control, reset, handleSubmit, watch } = methods;
    const calendarSubmitHandlerRef = useRef<null | (() => void)>(null);

    const [isReserveDisabled, setIsReserveDisabled] = useState(false);

    const spaceType = watch('spaceType');
    const reqMeetingRoom = watch('reqMeetingRoom');
    const notes = watch('notes');

    useEffect(() => {
        if (waitListRequest) {
            let spaceTypeObject;
            if (
                waitListRequest?.module?.type === 'commercial' &&
                waitListRequest.commercialWaitListRequestInfo
            ) {
                spaceTypeObject = mapCommercialUnitsToSpaceType(
                    waitListRequest?.commercialWaitListRequestInfo,
                );
            }
            reset({ ...waitListRequest, ...spaceTypeObject });
        }
    }, [waitListRequest]);

    const handleClose = () => {
        onClose();
        reset();
        setIsEditingWaitList(false);
    };

    const handleReserve = () => {
        calendarSubmitHandlerRef.current?.();
    };

    const onSubmit = async (data: FormValues) => {
        try {
            if (!id || !waitListRequest) return;

            const request = {
                module: {
                    type: waitListRequest?.module?.type,
                    id: waitListRequest?.module?.id,
                },
                ...data,
                ...(waitListRequest?.module?.type === 'commercial'
                    ? {
                          commercialWaitListRequestInfo:
                              mapSpaceTypeToCommercialUnits(
                                  spaceType,
                                  reqMeetingRoom,
                              ),
                      }
                    : {}),
            };

            await updateWaitListRequest([id, request]).unwrap();
            setIsEditingWaitList(false);
            toast.success('Request updated successfully');
        } catch (error) {
            toast.error('Error updating request');
        }
    };

    const options = useMemo(
        () => [
            { value: 'desk', label: 'Desk' },
            { value: 'privateOffice', label: 'Private Office' },
            { value: 'meetingRoom', label: 'Meeting Room' },
            { value: 'entireVenue', label: 'Entire Venue' },
        ],
        [],
    );

    const checkinOptions = TimeArray.filter(
        (time) => time.value >= experience?.checkinTime,
    );
    const checkoutOptions = TimeArray.filter(
        (time) => time.value <= experience?.checkoutTime,
    );

    const handleCancel = async (requestId: number) => {
        try {
            await cancelWaitListRequest(requestId).unwrap();
            onClose();
            toast.success('Request cancelled successfully');
        } catch (error) {
            toast.error('Error cancelling request');
        }
    };
    const isGuest = loggedInUser?.role?.includes('guest');
    const canEdit = waitListRequest?.status === 'active' && isGuest;

    const title = waitListRequest
        ? `Waitlist Request #${waitListRequest.id}`
        : 'Waitlist Request';

    const subtitle = waitListRequest
        ? `${experience?.name || ''}${waitListRequest?.status ? ` • ${camelCaseToSentenceCase(waitListRequest.status)}` : ''}`
        : '';

    if (!waitListRequest) {
        return null;
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen={isMobile}
            maxWidth='sm'
            fullWidth
        >
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {!waitListRequest ? (
                        <WaitListPreviewLoading />
                    ) : (
                        <>
                            <DialogTitle sx={{ alignItems: 'flex-start' }}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    {title}
                                    {subtitle ? (
                                        <Typography
                                            variant='body3'
                                            sx={{ opacity: 0.8 }}
                                        >
                                            {subtitle}
                                        </Typography>
                                    ) : null}
                                </Box>

                                <Box sx={{ display: 'flex' }}>
                                    {canEdit ? (
                                        <IconButton
                                            onClick={() =>
                                                setIsEditingWaitList(true)
                                            }
                                            size='small'
                                            sx={{ mr: 0.25 }}
                                            aria-label='Edit waitlist request'
                                        >
                                            <EditIcon fontSize='small' />
                                        </IconButton>
                                    ) : null}

                                    <IconButton
                                        onClick={handleClose}
                                        size='small'
                                        aria-label='Close'
                                        sx={{ color: 'text.primary' }}
                                    >
                                        <Close />
                                    </IconButton>
                                </Box>
                            </DialogTitle>

                            <DialogContent sx={{ minHeight: '240px', py: 2 }}>
                                <Stack spacing={2}>
                                    <Box
                                        sx={{
                                            borderRadius: 2,
                                            px: 1.5,
                                            py: 1.25,
                                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                            backgroundColor:
                                                theme.palette.custom
                                                    .cardActionBackground,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: {
                                                    xs: '140px 1fr',
                                                    sm: '160px 260px',
                                                },
                                                columnGap: 16,
                                                rowGap: 1,
                                                alignItems: 'center',
                                                '& .label': {
                                                    fontWeight: 900,
                                                    lineHeight: 1.35,
                                                },
                                                '& .value': {
                                                    lineHeight: 1.35,
                                                    justifySelf: 'start',
                                                    minWidth: 0,
                                                    width: '50%',
                                                },
                                                '& .value .MuiAutocomplete-root':
                                                    {
                                                        width: '100%',
                                                    },
                                                '& .value .MuiFormControl-root':
                                                    {
                                                        width: '100%',
                                                    },
                                            }}
                                        >
                                            <Typography
                                                className='label'
                                                variant='body3'
                                            >
                                                Requested by
                                            </Typography>
                                            <Typography
                                                className='value'
                                                variant='body3'
                                            >
                                                {capitalizeName(
                                                    waitListRequest.createdBy
                                                        ?.firstName,
                                                )}{' '}
                                                {capitalizeName(
                                                    waitListRequest.createdBy
                                                        ?.lastName,
                                                )}
                                            </Typography>
                                            <Typography
                                                className='label'
                                                variant='body3'
                                            >
                                                Company
                                            </Typography>
                                            <Typography
                                                className='value'
                                                variant='body3'
                                            >
                                                {capitalizeName(
                                                    waitListRequest.guest?.name,
                                                )}
                                            </Typography>
                                            <Typography
                                                className='label'
                                                variant='body3'
                                            >
                                                Date
                                            </Typography>
                                            <Typography
                                                className='value'
                                                variant='body3'
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    width: 'auto',
                                                }}
                                            >
                                                {`${moment(
                                                    new Date(
                                                        waitListRequest.startDate,
                                                    ),
                                                ).format(
                                                    'DD/MM/YYYY',
                                                )} - ${moment(
                                                    new Date(
                                                        waitListRequest.endDate,
                                                    ),
                                                ).format('DD/MM/YYYY')}`}
                                            </Typography>

                                            <Typography
                                                className='label'
                                                variant='body3'
                                            >
                                                Check-in
                                            </Typography>
                                            <Box className='value'>
                                                {isEditingWaitList ? (
                                                    <FormAutocomplete
                                                        name='checkinTime'
                                                        control={control}
                                                        options={checkinOptions}
                                                        fontSize='14px'
                                                    />
                                                ) : (
                                                    <Typography
                                                        variant='body3'
                                                        sx={{
                                                            lineHeight: 1.35,
                                                        }}
                                                    >
                                                        {formatTime(
                                                            waitListRequest?.checkinTime,
                                                        )}
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Typography
                                                className='label'
                                                variant='body3'
                                            >
                                                Checkout
                                            </Typography>
                                            <Box className='value'>
                                                {isEditingWaitList ? (
                                                    <FormAutocomplete
                                                        name='checkoutTime'
                                                        control={control}
                                                        options={
                                                            checkoutOptions
                                                        }
                                                        fontSize='14px'
                                                    />
                                                ) : (
                                                    <Typography
                                                        variant='body3'
                                                        sx={{
                                                            lineHeight: 1.35,
                                                        }}
                                                    >
                                                        {formatTime(
                                                            waitListRequest?.checkoutTime,
                                                        )}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {waitListRequest?.module?.type ===
                                            'commercial' ? (
                                                <>
                                                    <Typography
                                                        className='label'
                                                        variant='body3'
                                                    >
                                                        Workspace type
                                                    </Typography>
                                                    <Box className='value'>
                                                        {isEditingWaitList ? (
                                                            <FormAutocomplete
                                                                name='spaceType'
                                                                control={
                                                                    control
                                                                }
                                                                options={
                                                                    options
                                                                }
                                                            />
                                                        ) : (
                                                            <Typography
                                                                variant='body3'
                                                                sx={{
                                                                    lineHeight: 1.35,
                                                                }}
                                                            >
                                                                {spaceType
                                                                    ? camelCaseToSentenceCase(
                                                                          spaceType,
                                                                      )
                                                                    : ''}
                                                            </Typography>
                                                        )}
                                                    </Box>

                                                    {[
                                                        'desk',
                                                        'privateOffice',
                                                    ].includes(spaceType) ? (
                                                        <>
                                                            <Typography
                                                                className='label'
                                                                variant='body3'
                                                            >
                                                                Meeting room
                                                                required?
                                                            </Typography>
                                                            <Box className='value'>
                                                                {isEditingWaitList ? (
                                                                    <FormSelect
                                                                        name='reqMeetingRoom'
                                                                        control={
                                                                            control
                                                                        }
                                                                        defaultValue={
                                                                            false
                                                                        }
                                                                        options={[
                                                                            {
                                                                                value: true,
                                                                                label: 'Yes',
                                                                            },
                                                                            {
                                                                                value: false,
                                                                                label: 'No',
                                                                            },
                                                                        ]}
                                                                    />
                                                                ) : (
                                                                    <Typography
                                                                        variant='body3'
                                                                        sx={{
                                                                            lineHeight: 1.35,
                                                                        }}
                                                                    >
                                                                        {reqMeetingRoom
                                                                            ? 'Yes'
                                                                            : 'No'}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </>
                                                    ) : null}
                                                </>
                                            ) : null}
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            borderRadius: 2,
                                            px: 1.5,
                                            py: 1.25,
                                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                            backgroundColor:
                                                theme.palette.background.paper,
                                        }}
                                    >
                                        <Stack spacing={0.75}>
                                            <Typography
                                                variant='body3'
                                                sx={{ fontWeight: 900 }}
                                            >
                                                Notes
                                            </Typography>

                                            {isEditingWaitList ? (
                                                <FormTextField
                                                    name='notes'
                                                    control={control}
                                                    multiline
                                                    rows={4}
                                                    fullWidth
                                                    inputProps={{
                                                        maxLength: 300,
                                                    }}
                                                />
                                            ) : (
                                                <Typography
                                                    variant='body3'
                                                    sx={{
                                                        color: notes
                                                            ? theme.palette.text
                                                                  .primary
                                                            : theme.palette.text
                                                                  .secondary,
                                                        whiteSpace: 'pre-wrap',
                                                    }}
                                                >
                                                    {notes || '—'}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Box>

                                    {experience && (
                                        //using calendar submit functionality to avoid bypassing possible validations
                                        <Box>
                                            <CalendarComponent
                                                experience={experience}
                                                numberOfMonths={1}
                                                startMonth={
                                                    new Date(
                                                        waitListRequest.startDate,
                                                    )
                                                }
                                                waitListRequest={
                                                    waitListRequest
                                                }
                                                hidePrimaryActionButton
                                                onRegisterSubmitHandler={(
                                                    submitHandler,
                                                ) => {
                                                    calendarSubmitHandlerRef.current =
                                                        submitHandler;
                                                }}
                                                onPrimaryActionDisabledChange={
                                                    setIsReserveDisabled
                                                }
                                            />
                                        </Box>
                                    )}

                                    {!isGuest && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                            }}
                                        >
                                            <Typography
                                                variant='body3'
                                                sx={{
                                                    color: theme.palette.text
                                                        .secondary,
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                {' '}
                                                <InfoOutlined
                                                    sx={{ fontSize: '16px' }}
                                                />{' '}
                                                Guest will be automatically
                                                notified when the experience
                                                becomes available for the
                                                selected dates.
                                            </Typography>
                                            <Typography
                                                variant='body3'
                                                sx={{
                                                    color: theme.palette.text
                                                        .secondary,
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                {' '}
                                                <InfoOutlined
                                                    sx={{ fontSize: '16px' }}
                                                />{' '}
                                                The expired requests will be
                                                automatically archived.
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </DialogContent>

                            <Divider />
                            <DialogActions sx={{ px: 2.5, py: 2 }}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        gap: 1,
                                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    {canEdit && !isEditingWaitList ? (
                                        <CustomButton
                                            variant='contained'
                                            color='warning'
                                            onClick={() =>
                                                handleCancel(waitListRequest.id)
                                            }
                                            fullWidth={isMobile}
                                        >
                                            Cancel request
                                        </CustomButton>
                                    ) : null}
                                    {isEditingWaitList ? (
                                        <CustomButton
                                            variant='outlined'
                                            color='primary'
                                            onClick={() =>
                                                setIsEditingWaitList(false)
                                            }
                                            fullWidth={isMobile}
                                        >
                                            Back
                                        </CustomButton>
                                    ) : null}

                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {waitListRequest?.isAvailableToBook &&
                                        isGuest &&
                                        waitListRequest.status ===
                                            WaitListRequestStatus.Active &&
                                        !isEditingWaitList ? (
                                            <CustomButton
                                                variant='contained'
                                                color='primary'
                                                onClick={handleReserve}
                                                disabled={isReserveDisabled}
                                                fullWidth={isMobile}
                                                sx={{
                                                    ml: isMobile ? 0 : 'auto',
                                                }}
                                            >
                                                Reserve now
                                            </CustomButton>
                                        ) : null}
                                        {isEditingWaitList ? (
                                            <CustomButton
                                                variant='contained'
                                                color='primary'
                                                type='submit'
                                                fullWidth={isMobile}
                                            >
                                                Save
                                            </CustomButton>
                                        ) : null}
                                        <CustomButton
                                            variant='outlined'
                                            color='primary'
                                            onClick={handleClose}
                                            fullWidth={isMobile}
                                            sx={{ ml: isMobile ? 0 : 'auto' }}
                                        >
                                            Close
                                        </CustomButton>
                                    </Box>
                                </Box>
                            </DialogActions>
                        </>
                    )}
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default WaitListPreview;
